import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { CheckCircle2 } from 'lucide-react';
import { usePlatformBranding } from '../hooks/usePlatformBranding';

const ResetPassword = () => {
  const branding = usePlatformBranding();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const navigate = useNavigate();

  const LINK_INVALIDO = 'Este enlace ya no es válido (pudo caducar o abrirse dos veces). Solicita una nueva invitación desde el club.';

  useEffect(() => {
    // La sesión de recuperación se establece de forma ASÍNCRONA cuando el SDK
    // lee el enlace (detectSessionInUrl). Un getSession() de un solo tiro puede
    // correr antes de que termine, dando un falso "link inválido". Por eso:
    // 1) tomamos la sesión si ya está, y 2) escuchamos el evento de recuperación.
    let resolved = false;
    const markSession = () => { resolved = true; setHasSession(true); setError(null); };

    // 1) Enlace robusto contra prefetch/escáneres: si trae token_hash, lo canjeamos
    //    con verifyOtp (solo corre en el navegador real del usuario, no en escáneres
    //    que hacen GET sin ejecutar JS). 2) Si no, caemos al flujo por hash (implícito).
    const redeem = async () => {
      const params = new URLSearchParams(window.location.search);
      const tokenHash = params.get('token_hash');
      const type = params.get('type');
      if (tokenHash && type) {
        const { error: otpError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
        if (!otpError) { markSession(); return; }
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (session) markSession();
    };
    redeem();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && ['PASSWORD_RECOVERY', 'SIGNED_IN', 'INITIAL_SESSION'].includes(event)) {
        markSession();
      }
    });

    // Si tras unos segundos no llegó la sesión, el enlace fue consumido/expiró.
    const timer = setTimeout(() => {
      if (!resolved) setError(LINK_INVALIDO);
    }, 5000);

    return () => { subscription.unsubscribe(); clearTimeout(timer); };
  }, []);

  const buildPasswordError = (message) => {
    const lower = (message || '').toLowerCase();
    if (lower.includes('password')) {
      if (lower.includes('least') && lower.includes('characters')) {
        return 'La contraseña es demasiado corta.';
      }
      if (lower.includes('weak')) {
        return 'La contraseña es débil. Usa una combinación de letras, números y símbolos.';
      }
      if (lower.includes('uppercase') || lower.includes('lowercase') || lower.includes('number') || lower.includes('symbol')) {
        return 'La contraseña debe incluir mayúsculas, minúsculas, números y símbolos.';
      }
      return 'La contraseña no cumple con las reglas de seguridad.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validaciones
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    if (!hasSession) {
      setError(LINK_INVALIDO);
      return;
    }

    try {
      setLoading(true);

      // Actualizar contraseña
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      setSuccess(true);

      // Redirigir al login después de 2 segundos
      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (err) {
      console.error('Error setting password:', err);
      const friendly = buildPasswordError(err?.message);
      setError(friendly || `Error al establecer contraseña: ${err?.message || 'Intenta de nuevo.'}`);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: branding.bg }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-8">
            <div className="text-center">
              <img src={branding.logo} alt={branding.name} className="h-12 mx-auto mb-6 object-contain" />
              <CheckCircle2 className="h-14 w-14 mx-auto text-green-600 mb-3" />
              <h2 className="text-2xl font-bold mb-2">
                Contraseña establecida
              </h2>
              <p className="text-muted-foreground">
                Tu contraseña ha sido configurada exitosamente.
                <br />
                Redirigiendo al login...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: branding.bg }}>
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <img src={branding.logo} alt={branding.name} className="h-12 mx-auto mb-4 object-contain" />
            <h1 className="text-2xl font-bold mb-2">Establecer contraseña</h1>
            <p className="text-muted-foreground">
              Por favor establece tu contraseña para acceder al sistema
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            <p className="font-medium text-slate-700 mb-1">Reglas de contraseña</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Mínimo 6 caracteres.</li>
              <li>Si el sistema rechaza la contraseña, usa 8+ caracteres con mayúsculas, minúsculas, número y símbolo.</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Nueva Contraseña
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Mínimo 6 caracteres"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Confirmar Contraseña
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Repite tu contraseña"
                required
                disabled={loading}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Estableciendo...' : 'Establecer Contraseña'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;


